import { Alert, Badge, Button, Group, Paper, Stack, Text } from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  useInterruptQueue,
  usePauseAllQueues,
  usePauseQueue,
  useResumeQueue,
  useStopQueue,
} from "./useQueueControls";
import type { TaskQueueResponse } from "./api";
import { isNoRunnerConflict } from "./provisioning";
import { requestErrorText } from "./requestErrorText";

// Операторская панель **известной** очереди: индикация состояния
// паузы + кнопки «пауза» / «снять паузу» / «прервать» / «стоп»
// (ADR-0018). Ветку «очереди нет» держит `QueueSection` — сюда она не
// доходит.
//
// `queue` — текущее состояние очереди (из useQueue). `hasActiveTask`
// управляет доступностью «прервать»/«стоп» (нечего прерывать, если
// очередь свободна).
export function QueueControls({
  queue,
  hasActiveTask,
}: {
  readonly queue: TaskQueueResponse;
  readonly hasActiveTask: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const pauseMutation = usePauseQueue();
  const resumeMutation = useResumeQueue();
  const interruptMutation = useInterruptQueue();
  const stopMutation = useStopQueue();
  const pauseAllMutation = usePauseAllQueues();

  const isPaused = queue.paused_by_operator;
  const pausedByQuota = queue.paused_by_quota;

  const mutations = [
    pauseMutation,
    resumeMutation,
    interruptMutation,
    stopMutation,
    pauseAllMutation,
  ];
  const anyPending = mutations.some((mutation) => mutation.isPending);
  const errorMutation = mutations.find((mutation) => mutation.isError);

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>{t("queueControls.title")}</Text>
          <Group gap="xs">
            {isPaused ? (
              <Badge color="orange">{t("queueControls.statePaused")}</Badge>
            ) : (
              <Badge color="teal">{t("queueControls.stateRunning")}</Badge>
            )}
            {pausedByQuota ? (
              <Badge color="yellow" variant="light">
                {t("queueControls.stateQuotaPaused")}
              </Badge>
            ) : null}
          </Group>
        </Group>

        <Group>
          {isPaused ? (
            <Button
              variant="light"
              color="teal"
              loading={resumeMutation.isPending}
              disabled={anyPending && !resumeMutation.isPending}
              onClick={() => {
                resumeMutation.mutate();
              }}
            >
              {t("queueControls.resume")}
            </Button>
          ) : (
            <Button
              variant="light"
              color="orange"
              loading={pauseMutation.isPending}
              disabled={anyPending && !pauseMutation.isPending}
              onClick={() => {
                pauseMutation.mutate();
              }}
            >
              {t("queueControls.pause")}
            </Button>
          )}

          <Button
            variant="light"
            color="red"
            loading={interruptMutation.isPending}
            disabled={
              !hasActiveTask || (anyPending && !interruptMutation.isPending)
            }
            onClick={() => {
              interruptMutation.mutate("paused");
            }}
          >
            {t("queueControls.interrupt")}
          </Button>

          <Button
            color="red"
            loading={stopMutation.isPending}
            disabled={anyPending && !stopMutation.isPending}
            onClick={() => {
              stopMutation.mutate("paused");
            }}
          >
            {t("queueControls.stop")}
          </Button>

          <Button
            variant="default"
            loading={pauseAllMutation.isPending}
            disabled={anyPending && !pauseAllMutation.isPending}
            onClick={() => {
              pauseAllMutation.mutate();
            }}
          >
            {t("queueControls.pauseAll")}
          </Button>
        </Group>

        {errorMutation !== undefined ? (
          <Alert color="red" title={t("queueControls.errorTitle")}>
            {isNoRunnerConflict(errorMutation.error)
              ? t("queueControls.errorNoRunner")
              : requestErrorText(errorMutation.error, t)}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
